/* @refresh reset */
import React from 'react';
import { BrowserRouter, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import enTranslations from '@shopify/polaris/locales/en.json';
import { AppProvider } from '@shopify/polaris';
import { makeStyles } from '@mui/styles';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import './index.css';
import '@shopify/polaris/build/esm/styles.css';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Routes from './routes';
import ContextsProvider from './contexts';
import './App.css';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { MSAL_CLIENT_ID } from './sharedModules/utils/env';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    minHeight: '100vh',
    width: '100%',
    padding: 0,
    backgroundColor: 'background.default',
  },
  box: {
    width: '100%',
  },
  content: {},
}));

interface AppProps {
  msalInstance: PublicClientApplication;
}

/**
 * Polaris passes `url` and renders `<a href>` by default (full page load). That bypasses
 * React Router, so `<Prompt>` never runs. AppProvider's `linkComponent` is the supported
 * hook to use `<Link to>` instead — same pattern as Shopify's React Router examples.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Polaris forwards arbitrary anchor props
const AppProviderLink = React.forwardRef<HTMLAnchorElement, any>(({ url, external, children, ...rest }, ref) => {
  if (external) {
    return (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        href={url as string}
        target="_blank"
        rel="noopener noreferrer"
        {...rest}
      >
        {children}
      </a>
    );
  }
  return (
    <Link ref={ref} to={(url as string) || '/'} {...(rest as object)}>
      {children}
    </Link>
  );
});
AppProviderLink.displayName = 'AppProviderLink';

const App: React.FC<AppProps> = (props) => {
  const classes = useStyles();
  const theme = createTheme();
  const { msalInstance } = props;

  return (
    <BrowserRouter>
      <AppProvider i18n={enTranslations} linkComponent={AppProviderLink as any}>
        <MsalProvider instance={msalInstance}>
          <ThemeProvider theme={theme}>
            <ContextsProvider>
              <CssBaseline />
              <Helmet>
                <script>
                  {`
                window.onUsersnapLoad = function(api) {
                  api.init();
                };
              `}
                </script>
                <script
                  src="https://widget.usersnap.com/global/load/9ae6e140-c7ad-4860-adc1-258062488ed0?onload=onUsersnapLoad"
                  defer
                ></script>
              </Helmet>
              <Container className={classes.root} color="background" maxWidth="xl">
                <Box className={classes.box}>
                  <main role="main" className={classes.content}>
                    <Routes msalClientId={MSAL_CLIENT_ID} />
                  </main>
                </Box>
              </Container>
            </ContextsProvider>
          </ThemeProvider>
        </MsalProvider>
      </AppProvider>
    </BrowserRouter>
  );
};

export default App;
